/**
 * Decision-record export — JSON + printable summary.
 *
 * v1 ships JSON export (deterministic, signature-stable) and a
 * plain-text printable summary suitable for browser-side print-to-
 * PDF. A native PDF generator is deferred to v2 once the audit
 * pipeline's format requirements are confirmed with John K3.
 *
 * @module gibbs/decision-records/export
 */

import { canonicalize } from './signature';

/**
 * Export a decision record as deterministic JSON. Sorted keys at
 * every level so byte-identical inputs produce byte-identical output
 * — required for the SHA-256 signature to round-trip across export/
 * re-import.
 *
 * @param {Object} record
 * @returns {string} deterministic JSON
 */
export function exportAsJson(record) {
    if (!record || typeof record !== 'object') {
        throw new Error('exportAsJson requires a record object');
    }
    return canonicalize(record);
}

/**
 * Render a printable plain-text summary of a decision record. Layout
 * mirrors the audit-trail summary chemists typically attach to grant
 * documentation: reviewer + timestamp at top, decision context in the
 * middle, signature line at the bottom.
 *
 * Suitable for browser-side `window.print()` (CSS @media print picks
 * it up cleanly). A future v2 may swap in a richer renderer (jsPDF
 * or pandoc) once format requirements settle.
 *
 * @param {Object} record
 * @returns {string} multi-line plain text
 */
export function exportAsPrintable(record) {
    if (!record || typeof record !== 'object') {
        throw new Error('exportAsPrintable requires a record object');
    }
    const lines = [];
    lines.push('DECISION RECORD');
    lines.push('================');
    lines.push('');
    lines.push(`Record ID:   ${record.id || '(none)'}`);
    lines.push(`Created:     ${record.createdAt || '(unknown)'}`);
    lines.push(`Schema:      v${record.schemaVersion || '?'}`);
    lines.push('');

    lines.push('Reviewer');
    lines.push('--------');
    lines.push(`  ID:           ${record.reviewer?.id || '(unknown)'}`);
    if (record.reviewer?.displayName) {
        lines.push(`  Display name: ${record.reviewer.displayName}`);
    }
    if (record.reviewer?.role) {
        lines.push(`  Role:         ${record.reviewer.role}`);
    }
    lines.push('');

    lines.push('Workspace');
    lines.push('---------');
    if (record.workspace?.proteinPdbId) {
        lines.push(`  Protein PDB:  ${record.workspace.proteinPdbId}`);
    }
    if (record.workspace?.projectName) {
        lines.push(`  Project:      ${record.workspace.projectName}`);
    }
    if (!record.workspace?.proteinPdbId && !record.workspace?.projectName) {
        lines.push('  (no workspace metadata)');
    }
    lines.push('');

    lines.push('Decision context');
    lines.push('----------------');
    const ctx = record.decisionContext || {};
    lines.push(`  Trigger surface:  ${ctx.triggerSurface || '(unknown)'}`);
    if (ctx.frame) {
        lines.push(`  Frame:            ${ctx.frame}`);
    }
    lines.push(`  User picked:      ${formatPicked(ctx.userPicked)}`);
    if (Array.isArray(ctx.rankedCompounds) && ctx.rankedCompounds.length > 0) {
        lines.push('  Compounds considered:');
        for (const c of ctx.rankedCompounds) {
            const parts = [`id=${c.id}`];
            if (typeof c.primaryScore === 'number') {
                parts.push(`primaryScore=${c.primaryScore.toFixed(2)}`);
            }
            if (Array.isArray(c.flags)) {
                parts.push(`flags=[${c.flags.length}]`);
            } else if (c.flags === null) {
                parts.push('flags=unknown');
            }
            lines.push(`    - ${parts.join(', ')}`);
        }
    }
    if (ctx.rationaleSnapshot) {
        lines.push('  Rationale (snapshot):');
        const rationaleStr = typeof ctx.rationaleSnapshot === 'string'
            ? ctx.rationaleSnapshot
            : (ctx.rationaleSnapshot.summary || JSON.stringify(ctx.rationaleSnapshot).slice(0, 200));
        lines.push(`    ${rationaleStr}`);
    }
    lines.push('');

    lines.push('Reviewer note');
    lines.push('-------------');
    lines.push(record.reviewerNote || '(none)');
    lines.push('');

    lines.push('Signature (SHA-256 content hash)');
    lines.push('--------------------------------');
    lines.push(record.signature || '(unsigned)');
    lines.push('');

    return lines.join('\n');
}

function formatPicked(picked) {
    if (picked === 'none-of-the-above') return 'none of the above';
    if (picked === 'defer') return 'deferred';
    if (typeof picked === 'number') return `compound id ${picked}`;
    return String(picked);
}

/**
 * Trigger a browser-side download of the exported record. No-op in
 * Node (test environments); browser-only path.
 *
 * @param {Object} record
 * @param {'json' | 'printable'} [format='json']
 */
export function downloadDecisionRecord(record, format = 'json') {
    if (typeof globalThis === 'undefined'
        || typeof globalThis.document === 'undefined') {
        // Node / non-browser — caller should use the *AsXxx functions directly.
        // eslint-disable-next-line no-console
        console.warn(
            '[decision-records] downloadDecisionRecord is browser-only.'
        );
        return;
    }
    const body = format === 'printable'
        ? exportAsPrintable(record)
        : exportAsJson(record);
    const mime = format === 'printable' ? 'text/plain' : 'application/json';
    const blob = new globalThis.Blob([body], { type: `${mime};charset=utf-8` });
    const url = globalThis.URL.createObjectURL(blob);
    const a = globalThis.document.createElement('a');
    a.href = url;
    const ext = format === 'printable' ? 'txt' : 'json';
    a.download = `decision-record-${record.id || 'export'}.${ext}`;
    globalThis.document.body.appendChild(a);
    a.click();
    globalThis.document.body.removeChild(a);
    globalThis.URL.revokeObjectURL(url);
}
